// Every edit the web makes, as sync changes. Same rules as the Mac (collector/Sources/TallyStore/Editing.swift):
// an edited entry keeps its previous state as a version, deletes are soft, corrections of agent time are
// append-only, and a session's project and title carry `editedAt` so the recording device cannot undo them.
import { defaultSessionTitle, ruleMatches } from '../../../packages/core/src/index.ts';
import type { AgentSession, Change, Client, Project, ProjectRule, SpanEdit, SyncedSettingKey, TimeEntry, TimeEntryVersion } from '../api/types.ts';
import { activeProjects, isOwner, type Workspace } from './data.ts';

export interface Actor {
  workspaceId: string;
  memberId: string;
  memberName: string;
  timeZone: string;
}

export const newId = (): string => crypto.randomUUID();

// MARK: Entries

/** The fields that make a new version when they change. */
function content(e: TimeEntry): string {
  return JSON.stringify([e.projectId, e.description, e.startAt, e.endAt, e.timeZone, e.billable, e.tags, e.deletedAt]);
}

export function newEntry(actor: Actor, now: number, fields: { description: string; projectId: string | null; start: number; end: number | null; billable: boolean; tags?: string[] }): TimeEntry {
  return {
    id: newId(), memberId: actor.memberId, projectId: fields.projectId, description: fields.description, startAt: fields.start, endAt: fields.end,
    timeZone: actor.timeZone, billable: fields.billable, source: 'human', version: 1, tags: fields.tags ?? [], createdAt: now, updatedAt: now, deletedAt: null,
  };
}

/** Saves `next` if anything changed; the previous state becomes a version first. New entries are simply created. */
export function saveEntry(actor: Actor, previous: TimeEntry | undefined, next: TimeEntry, now: number): Change[] {
  if (!previous) return [{ entity: 'timeEntries', op: 'upsert', data: { ...next, createdAt: next.createdAt || now, updatedAt: now } }];
  if (content(previous) === content(next)) return [];
  const version: TimeEntryVersion = {
    id: newId(), entryId: previous.id, version: previous.version, snapshot: { ...previous, workspaceId: actor.workspaceId }, editedAt: now,
  };
  return [
    { entity: 'timeEntryVersions', op: 'upsert', data: version },
    { entity: 'timeEntries', op: 'upsert', data: { ...next, version: previous.version + 1, createdAt: previous.createdAt, updatedAt: now } },
  ];
}

/** Timer changes (start, stop, the running entry's text or project) are not history, but they do count as a version. */
function touch(entry: TimeEntry, fields: Partial<TimeEntry>, now: number): Change {
  return { entity: 'timeEntries', op: 'upsert', data: { ...entry, ...fields, version: entry.version + 1, updatedAt: now } };
}

/** A timer always runs for an active project. */
export function canStartTimer(project: Project | undefined): project is Project {
  return !!project && project.deletedAt === null && project.archivedAt === null;
}

/** Starts a timer for an active project (else no change); a running one stops first (one timer per person). */
export function startTimer(actor: Actor, running: TimeEntry | undefined, draft: { description: string; project: Project | undefined }, now: number): Change[] {
  const project = draft.project;
  if (!canStartTimer(project)) return [];
  const changes: Change[] = [];
  if (running) changes.push(touch(running, { endAt: Math.max(now, running.startAt) }, now));
  changes.push({ entity: 'timeEntries', op: 'upsert', data: newEntry(actor, now, {
    description: draft.description.trim(), projectId: project.id, start: now, end: null, billable: project.billable,
  }) });
  return changes;
}

export function stopTimer(running: TimeEntry, now: number): Change[] {
  return [touch(running, { endAt: Math.max(now, running.startAt) }, now)];
}

export function updateRunning(running: TimeEntry, fields: { description?: string; project?: Project | null }, now: number): Change[] {
  const next: Partial<TimeEntry> = {};
  if (fields.description !== undefined) next.description = fields.description;
  if (fields.project !== undefined) {
    next.projectId = fields.project?.id ?? null;
    next.billable = fields.project?.billable ?? true;
  }
  return [touch(running, next, now)];
}

/** Splits an entry at `at`: it ends there and a copy continues until the old end (or keeps running). */
export function splitEntry(actor: Actor, entry: TimeEntry, at: number, now: number): Change[] {
  if (entry.deletedAt !== null || at <= entry.startAt || at >= (entry.endAt ?? now)) return [];
  const second: TimeEntry = { ...entry, id: newId(), startAt: at, version: 1, createdAt: now, updatedAt: now };
  return [...saveEntry(actor, entry, { ...entry, endAt: at }, now), { entity: 'timeEntries', op: 'upsert', data: second }];
}

/** Soft delete: kept as a version, restorable from the history. */
export function deleteEntries(actor: Actor, entries: readonly TimeEntry[], now: number): Change[] {
  return entries.filter((e) => e.deletedAt === null).flatMap((e) =>
    saveEntry(actor, e, { ...e, endAt: e.endAt ?? Math.max(e.startAt, now), deletedAt: now }, now));
}

export function bulkUpdate(actor: Actor, entries: readonly TimeEntry[], change: (e: TimeEntry) => TimeEntry, now: number): Change[] {
  return entries.filter((e) => e.deletedAt === null).flatMap((e) => saveEntry(actor, e, change(e), now));
}

/** The entry a version snapshot describes; devices write tags as JSON text, the server and the web as an array. */
export function snapshotEntry(snapshot: Record<string, unknown>, current: TimeEntry): TimeEntry {
  const s = snapshot;
  const num = (v: unknown, fallback: number | null) => (typeof v === 'number' ? v : fallback);
  let tags: string[] = current.tags;
  if (Array.isArray(s.tags)) tags = s.tags.filter((x): x is string => typeof x === 'string');
  else if (typeof s.tags === 'string') {
    try {
      const parsed: unknown = JSON.parse(s.tags);
      if (Array.isArray(parsed)) tags = parsed.filter((x): x is string => typeof x === 'string');
    } catch { /* keep the current tags */ }
  }
  return {
    ...current,
    projectId: typeof s.projectId === 'string' ? s.projectId : null,
    description: typeof s.description === 'string' ? s.description : '',
    startAt: num(s.startAt, current.startAt) as number,
    endAt: num(s.endAt, null),
    timeZone: typeof s.timeZone === 'string' ? s.timeZone : current.timeZone,
    billable: typeof s.billable === 'boolean' ? s.billable : current.billable,
    tags,
    deletedAt: num(s.deletedAt, null),
  };
}

/** Brings back a version (itself an edit, undoable the same way). Only one timer may run. */
export function restoreVersion(actor: Actor, version: TimeEntryVersion, current: TimeEntry, anotherRunning: boolean, now: number): Change[] {
  const old = snapshotEntry(version.snapshot, current);
  if (old.endAt === null && old.deletedAt === null && anotherRunning) old.endAt = Math.max(old.startAt, now);
  return saveEntry(actor, current, old, now);
}

// MARK: Projects

/** The first color slot no active project uses, in order 1–8. Fixed forever after. */
export function nextColorSlot(ws: Workspace): number {
  const used = new Set(activeProjects(ws).map((p) => p.colorSlot));
  for (let slot = 1; slot <= 8; slot++) if (!used.has(slot)) return slot;
  return (ws.projects.length % 8) + 1;
}

export interface ProjectDraft {
  name: string;
  clientName: string;
  rateCents: number | null;
  billable: boolean;
  roundingMinutes: number;
  /** A new client takes the project's rate as its default (not when the rate is only kept, without Pro). */
  clientTakesRate?: boolean;
}

export function saveProject(ws: Workspace, draft: ProjectDraft, existing: Project | undefined, now: number): { changes: Change[]; project: Project } {
  const changes: Change[] = [];
  let clientId: string | null = null;
  const clientName = draft.clientName.trim();
  if (clientName) {
    const known = ws.clients.find((c) => c.name === clientName && c.deletedAt === null);
    if (known) clientId = known.id;
    else {
      const c: Client = { id: newId(), name: clientName, defaultRateCents: draft.clientTakesRate === false ? null : draft.rateCents, currency: 'EUR', createdAt: now, updatedAt: now, deletedAt: null };
      changes.push({ entity: 'clients', op: 'upsert', data: c });
      clientId = c.id;
    }
  }
  const base: Project = existing ?? {
    id: newId(), clientId: null, name: '', colorSlot: nextColorSlot(ws), rateCents: null, currency: 'EUR', billable: true, roundingMinutes: 0,
    archivedAt: null, createdAt: now, updatedAt: now, deletedAt: null,
  };
  const project: Project = {
    ...base, name: draft.name.trim(), clientId, rateCents: draft.rateCents, billable: draft.billable, roundingMinutes: draft.roundingMinutes, updatedAt: now,
  };
  changes.push({ entity: 'projects', op: 'upsert', data: project });
  return { changes, project };
}

export function archiveProject(p: Project, archived: boolean, now: number): Change[] {
  return [{ entity: 'projects', op: 'upsert', data: { ...p, archivedAt: archived ? now : null, updatedAt: now } }];
}

/** "95", "95,50", "1.250,00", "95.5" → cents; empty → no rate; `undefined` if unreadable. */
export function parseAmount(text: string): number | null | undefined {
  const t = text.replace('€', '').trim();
  if (!t) return null;
  let normalized = t;
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(t)) normalized = t.replaceAll('.', '').replace(',', '.');
  else if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(t)) normalized = t.replaceAll(',', '');
  else normalized = t.replace(',', '.');
  const value = Number(normalized);
  return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : undefined;
}

export function addRule(ws: Workspace, projectId: string, kind: 'remote' | 'branch', pattern: string, now: number): Change[] {
  const trimmed = pattern.trim();
  if (!trimmed || ws.rules.some((r) => r.projectId === projectId && r.kind === kind && r.pattern === trimmed)) return [];
  const rule: ProjectRule = { id: newId(), projectId, kind, pattern: trimmed, priority: 0, createdAt: now, updatedAt: now, deletedAt: null };
  return [{ entity: 'projectRules', op: 'upsert', data: rule }];
}

export function deleteRule(rule: ProjectRule): Change[] {
  return [{ entity: 'projectRules', op: 'delete', id: rule.id }];
}

// MARK: Sessions

/**
 * Assigns a session (and its subagent threads) to a project. `remember` adds a rule for its git remote (the web
 * never sees working directories, so only sessions with a synced remote can be remembered) and assigns the other
 * loaded sessions of that repository that have no project yet.
 */
export function assignSession(ws: Workspace, session: AgentSession, projectId: string, remember: boolean, loaded: readonly AgentSession[], now: number): Change[] {
  const edit = (s: AgentSession, source: 'manual' | 'rule'): Change =>
    ({ entity: 'agentSessions', op: 'upsert', data: { ...s, projectId, projectSource: source, editedAt: now, updatedAt: now } });
  const changes = [session, ...loaded.filter((s) => s.parentId === session.id)].map((s) => edit(s, 'manual'));
  if (remember && isOwner(ws) && session.gitRemote) {
    const rule = addRule(ws, projectId, 'remote', session.gitRemote, now);
    changes.push(...rule);
    const pattern = session.gitRemote;
    for (const other of loaded) {
      if (other.id === session.id || other.parentId === session.id || other.projectId !== null) continue;
      if (ruleMatches({ kind: 'remote', pattern }, { remote: other.gitRemote, branch: other.gitBranch })) changes.push(edit(other, 'rule'));
    }
  }
  return changes;
}

/** A manual title wins over everything; an empty one goes back to "repo · branch". */
export function renameSession(session: AgentSession, title: string, now: number): Change[] {
  const trimmed = title.trim().slice(0, 120);
  const fallback = defaultSessionTitle(session.repoName, session.gitBranch);
  const data: AgentSession = trimmed
    ? { ...session, title: trimmed, titleSource: 'manual', editedAt: now, updatedAt: now }
    : { ...session, title: fallback, titleSource: fallback ? 'default' : null, editedAt: now, updatedAt: now };
  return [{ entity: 'agentSessions', op: 'upsert', data }];
}

/** Deletes a session with its events, spans, corrections and tokens on every device (GDPR); subagents go along. */
export function deleteSession(session: AgentSession, loaded: readonly AgentSession[]): Change[] {
  return [...loaded.filter((s) => s.parentId === session.id), session].map((s): Change => ({ entity: 'agentSessions', op: 'delete', id: s.id }));
}

/** Corrections (spec §9a): reassign or discard part of a session's working time. Nothing is overwritten. */
export function correctSession(actor: Actor, sessionId: string, kind: 'reassign' | 'discard', range: { start: number; end: number },
  projectId: string | null, note: string | null, now: number): Change[] {
  if (range.end <= range.start || (kind === 'reassign' && !projectId)) return [];
  const edit: SpanEdit = {
    id: newId(), sessionId, kind, startAt: range.start, endAt: range.end, projectId: kind === 'reassign' ? projectId : null,
    note: note?.trim() ? note.trim().slice(0, 500) : null, createdAt: now, createdBy: actor.memberName || null, revertedAt: null, updatedAt: now,
  };
  return [{ entity: 'spanEdits', op: 'upsert', data: edit }];
}

export function revertCorrection(edit: SpanEdit, now: number): Change[] {
  if (edit.revertedAt !== null) return [];
  return [{ entity: 'spanEdits', op: 'upsert', data: { ...edit, revertedAt: now, updatedAt: now } }];
}

// MARK: Settings

export function setSetting(key: SyncedSettingKey, value: string | null, now: number): Change[] {
  return value === null ? [{ entity: 'settings', op: 'delete', id: key }] : [{ entity: 'settings', op: 'upsert', data: { key, value, updatedAt: now } }];
}
